import { useEffect, useState } from 'react';
import { subscriptionsApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { dataTablePageProps, paginateClientList, usePagination } from '@/hooks/usePagination';
import type { SubscriptionPlanResponse } from '@/types/subscription';
import { formatCurrency } from '@/utils/format';
import { showApiError } from '@/utils/apiError';

export function PlansPage() {
  const pagination = usePagination();
  const [plans, setPlans] = useState<SubscriptionPlanResponse[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    subscriptionsApi
      .listPlans()
      .then((rows) => {
        setPlans(rows);
        pagination.applyClientCount(rows.length);
      })
      .catch((err) => showApiError(err, 'Failed to load plans'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once; pagination is client-side only
  }, []);

  const columns: Column<SubscriptionPlanResponse>[] = [
    { key: 'planCode', header: 'Code' },
    { key: 'name', header: 'Name' },
    { key: 'description', header: 'Description' },
    {
      key: 'priceMonthly',
      header: 'Monthly',
      render: (r) => formatCurrency(r.priceMonthly),
    },
    {
      key: 'priceYearly',
      header: 'Yearly',
      render: (r) => formatCurrency(r.priceYearly),
    },
    { key: 'maxStores', header: 'Max Stores' },
    { key: 'maxEmployees', header: 'Max Employees' },
    { key: 'maxProducts', header: 'Max Products' },
  ];

  return (
    <div>
      <PageHeader
        title="Subscription Plans"
        breadcrumbs={[{ label: 'Master', to: '/master' }, { label: 'Plans' }]}
      />
      <DataTable
        columns={columns}
        data={paginateClientList(plans, pagination.page, pagination.size)}
        keyField="id"
        loading={loading}
        {...dataTablePageProps(pagination)}
      />
    </div>
  );
}
